
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Send, Bot, User, Trash2, Lightbulb, TrendingUp, AlertTriangle, TestTube2, Shield, Activity, Target } from 'lucide-react';
import { useAppContext } from '../contexts/AppContext';
import { chatWithAI } from '../services/geminiService';
import { getSuggestedPrompts, SuggestedPrompt, CategoryType } from '../constants/suggestedPrompts';
import ExecutiveReport from '../components/ExecutiveReport';
import ErrorBoundary from '../components/ErrorBoundary';
import ReportModal from '../components/ReportModal';
import LoadingOverlay from '../components/LoadingOverlay';
import { ModuleInfoModal } from '../components/ModuleInfoModal';

interface Message {
    id: string;
    role: 'user' | 'ai';
    content: string;
    timestamp: number;
}


const CATEGORIES = [
    {
        id: 'Project Status' as CategoryType,
        icon: <Target className="text-indigo-500" />,
        color: 'bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100',
        activeColor: 'bg-indigo-500 text-white',
        description: 'High-level project overview and status'
    },
    {
        id: 'Reliability' as CategoryType,
        icon: <Activity className="text-blue-500" />,
        color: 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100',
        activeColor: 'bg-blue-500 text-white',
        description: 'Overall system reliability analysis'
    },
    {
        id: 'Current Risks' as CategoryType,
        icon: <AlertTriangle className="text-red-500" />,
        color: 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100',
        activeColor: 'bg-red-500 text-white',
        description: 'Risk assessment and standard-specific metrics'
    },
    {
        id: 'Risk Reduction' as CategoryType,
        icon: <TrendingUp className="text-green-500" />,
        color: 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100',
        activeColor: 'bg-green-500 text-white',
        description: 'Historical progress and actions taken'
    },
    {
        id: 'LIMS Feedback' as CategoryType,
        icon: <TestTube2 className="text-pink-500" />,
        color: 'bg-pink-50 border-pink-200 text-pink-700 hover:bg-pink-100',
        activeColor: 'bg-pink-500 text-white',
        description: 'Test verification and risk contribution'
    },
    {
        id: 'Certification & Compliance' as CategoryType,
        icon: <Shield className="text-purple-500" />,
        color: 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100',
        activeColor: 'bg-purple-500 text-white',
        description: 'Verification of standard-specific requirements'
    }
];

// Initial prompts moved to suggestedPrompts.ts

const ChatbotPage: React.FC = () => {
    const { allData, activeProject } = useAppContext();
    const [messages, setMessages] = useState<Message[]>([
        { id: '1', role: 'ai', content: 'Hello! I am your Reliability Engineering Assistant. I have access to your FMEA, BOM, and DVP&R data.\n\n💡 Select a category on the right to see specialized prompts for detailed analysis and reporting!', timestamp: Date.now() }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState<CategoryType>('Project Status');
    const [modalReport, setModalReport] = useState<string | null>(null);
    const [currentQuery, setCurrentQuery] = useState<string>('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Dynamic suggested prompts based on standard
    const suggestedPrompts = useMemo(() => {
        const standard = allData.projectMetadata?.fmeaStandard || 'AIAG_VDA';
        return getSuggestedPrompts(standard);
    }, [allData.projectMetadata?.fmeaStandard]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSend = async (promptText?: string, e?: React.FormEvent) => {
        if (e) e.preventDefault();
        const messageText = promptText || input;
        if (!messageText.trim() || isLoading) return;

        const userMessage: Message = {
            id: Date.now().toString(),
            role: 'user',
            content: messageText,
            timestamp: Date.now()
        };

        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);

        try {
            // Trim context to only relevant data for the active project
            const trimmedContext = {
                activeProject,
                projectMetadata: allData.projectMetadata,
                projectContext: allData.projectContext,
                // Only send data related to the active project to reduce payload
                fmeaItems: allData.fmeaItems?.filter(i => !activeProject || activeProject === 'all' || i.projectName === activeProject),
                bomItems: allData.bomItems?.filter(i => !activeProject || activeProject === 'all' || i.projectName === activeProject),
                customerRequirements: allData.customerRequirements?.filter(r => !activeProject || activeProject === 'all' || (r as any).projectName === activeProject),
                pDiagramList: allData.pDiagramList?.filter(d => !activeProject || activeProject === 'all' || d.projectName === activeProject),
                botId: 'BOT-ANALYST-01',
                pageName: 'AI Assistant',
                pagePurpose: 'Executive Analysis and Reporting'
            };

            const response = await chatWithAI(messageText, trimmedContext);

            // Log response for debugging
            console.log('✅ AI Response received:', response.substring(0, 100) + '...');

            const aiMessage: Message = {
                id: (Date.now() + 1).toString(),
                role: 'ai',
                content: response,
                timestamp: Date.now()
            };
            setMessages(prev => [...prev, aiMessage]);

            // Open modal with report
            setCurrentQuery(messageText);
            setModalReport(response);
            setIsModalOpen(true);
        } catch {
            const errorMessage: Message = {
                id: (Date.now() + 1).toString(),
                role: 'ai',
                content: "Sorry, I encountered an error processing your request.",
                timestamp: Date.now()
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePromptClick = (prompt: SuggestedPrompt) => {
        handleSend(prompt.prompt);
    };

    const clearChat = () => {
        setMessages([
            { id: '1', role: 'ai', content: 'Hello! I am your Reliability Engineering Assistant. I have access to your FMEA, BOM, and DVP&R data.\n\n💡 Select a category on the right to see specialized prompts for detailed analysis and reporting!', timestamp: Date.now() }
        ]);
    };

    const filteredPrompts = suggestedPrompts.filter(p => p.category === selectedCategory);
    const currentCategory = CATEGORIES.find(c => c.id === selectedCategory);

    return (
        <div className="flex gap-4 h-[calc(100vh-8rem)]">
            {/* Main Chat Area */}
            <div className="flex-1 flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h1 className="text-3xl font-bold text-text-primary flex items-center gap-2">
                            <Bot className="text-primary" /> AI Assistant
                        </h1>
                        <p className="text-text-secondary">Ask questions about your FMEA, potential failure modes, and design controls.</p>
                    </div>
                    <div className="flex gap-2">
                        <ModuleInfoModal
                            title="AI Reliability Assistant"
                            description="Your intelligent partner for reliability engineering. This assistant uses advanced AI to analyze your project data, generate reports, and answer complex technical questions."
                            description2="It considers FMEA risks, LIMS test data, and BOM materials to provide holistic answers."
                            benefits={[
                                "Instant access to all project data (FMEA, Tests, Materials) via natural language.",
                                "Automated report generation with Executive Summaries and Recommendations.",
                                "Deep insights into cross-functional risks (e.g., how material choices affect failure modes).",
                                "Drafting of new FMEA items and DVP&R plans based on system descriptions."
                            ]}
                            usageInstructions={[
                                "Type a question in the chat box, e.g., 'What are the top risks for the motor?'.",
                                "Use the 'Suggested Prompts' categories above the chat to quickly generate common reports.",
                                "When a report is generated, click the 'Print' icon to create a PDF or 'Copy' to paste it elsewhere."
                            ]}
                        />
                        <button
                            onClick={() => setShowSuggestions(!showSuggestions)}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm text-primary hover:bg-primary/10 rounded transition-colors border border-primary/20"
                        >
                            <Lightbulb size={16} /> {showSuggestions ? 'Hide' : 'Show'} Prompts
                        </button>
                        <button
                            onClick={clearChat}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-500 hover:bg-red-50 rounded transition-colors border border-red-200"
                        >
                            <Trash2 size={16} /> Clear Chat
                        </button>
                    </div>
                </div>

                <div className="flex-1 bg-card border border-border rounded-lg shadow-sm overflow-hidden flex flex-col">
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                        {messages.map((msg) => (
                            <div
                                key={msg.id}
                                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                            >
                                {msg.role === 'user' ? (
                                    <div className="max-w-[80%] rounded-lg p-3 bg-primary text-white rounded-br-none">
                                        <div className="flex items-center gap-2 mb-1 opacity-70 text-xs">
                                            <User size={12} />
                                            <span>You</span>
                                        </div>
                                        <div className="whitespace-pre-wrap text-sm leading-relaxed">
                                            {msg.content}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="max-w-[95%] w-full">
                                        <div className="flex items-center gap-2 mb-3 text-xs text-text-secondary">
                                            <Bot size={14} className="text-primary" />
                                            <span className="font-medium">AI Assistant - Executive Analysis</span>
                                            <span className="ml-auto text-xs opacity-60">
                                                {new Date(msg.timestamp).toLocaleTimeString()}
                                            </span>
                                        </div>
                                        <ErrorBoundary>
                                            <ExecutiveReport content={msg.content} />
                                        </ErrorBoundary>
                                    </div>
                                )}
                            </div>
                        ))}
                        {isLoading && (
                            <div className="flex justify-start">
                                <div className="bg-background border border-border rounded-lg p-3 rounded-bl-none">
                                    <div className="flex gap-1">
                                        <div className="w-2 h-2 bg-text-secondary/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                        <div className="w-2 h-2 bg-text-secondary/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                        <div className="w-2 h-2 bg-text-secondary/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    <div className="p-4 border-t border-border bg-background/50">
                        <form onSubmit={(e) => handleSend(undefined, e)} className="flex gap-2">
                            <input
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder="Ask about failure modes, risks, or DVP&R..."
                                className="flex-1 bg-background border border-border rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                                disabled={isLoading}
                            />
                            <button
                                type="submit"
                                disabled={isLoading || !input.trim()}
                                className="bg-primary text-white p-2 rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <Send size={20} />
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            {/* Suggested Prompts Sidebar */}
            {showSuggestions && (
                <div className="w-96 flex flex-col">
                    <div className="mb-3">
                        <h2 className="text-lg font-bold text-text-primary flex items-center gap-2">
                            <Lightbulb className="text-yellow-500" /> Suggested Prompts
                        </h2>
                        <p className="text-xs text-text-secondary mt-1">Select a category, then click any prompt</p>
                    </div>

                    {/* Category Selection */}
                    <div className="grid grid-cols-2 gap-2 mb-4">
                        {CATEGORIES.map((cat) => (
                            <button
                                key={cat.id}
                                onClick={() => setSelectedCategory(cat.id)}
                                className={`flex items-center gap-2 p-3 rounded-lg border transition-all text-left ${selectedCategory === cat.id
                                    ? cat.activeColor + ' shadow-md'
                                    : 'bg-card border-border text-text-secondary hover:border-primary/30'
                                    }`}
                            >
                                <div className={selectedCategory === cat.id ? 'text-white' : ''}>
                                    {cat.icon}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-xs truncate">{cat.id}</div>
                                    <div className="text-[10px] opacity-75 truncate">{cat.description.split(' ').slice(0, 3).join(' ')}</div>
                                </div>
                            </button>
                        ))}
                    </div>

                    {/* Category Description */}
                    <div className={`p-3 rounded-lg mb-3 border ${currentCategory?.color}`}>
                        <div className="flex items-center gap-2 mb-1">
                            {currentCategory?.icon}
                            <span className="font-bold text-sm">{selectedCategory}</span>
                        </div>
                        <p className="text-xs">{currentCategory?.description}</p>
                        <div className="text-xs mt-2 font-semibold">{filteredPrompts.length} prompts available</div>
                    </div>

                    {/* Filtered Prompts */}
                    <div className="flex-1 overflow-y-auto space-y-2 pr-2">
                        {filteredPrompts.map((prompt, index) => (
                            <button
                                key={prompt.id}
                                onClick={() => handlePromptClick(prompt)}
                                disabled={isLoading}
                                className="w-full text-left bg-card border border-border rounded-lg p-3 hover:border-primary/50 hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
                            >
                                <div className="flex items-start gap-2 mb-1.5">
                                    <div className="text-primary/70 mt-0.5">{prompt.icon}</div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center shrink-0">
                                                {index + 1}
                                            </span>
                                            <div className="font-semibold text-xs text-text-primary group-hover:text-primary transition-colors truncate">
                                                {prompt.title}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <p className="text-xs text-text-secondary line-clamp-2 ml-7">
                                    {prompt.description}
                                </p>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Loading Overlay */}
            {isLoading && <LoadingOverlay />}

            {/* Report Modal */}
            <ReportModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title="Executive Analysis Report"
                query={currentQuery}
            >
                {modalReport && (
                    <ErrorBoundary>
                        <ExecutiveReport content={modalReport} />
                    </ErrorBoundary>
                )}
            </ReportModal>
        </div>
    );
};

export default ChatbotPage;
